// A minimal transient-notification store. Optimistic mutations that fail roll the cache back and
// surface the SERVER error verbatim through a toast — the CRIT-111 phantom-success lesson: a mutation never
// reports success on an unpersisted write. This is UI state ONLY (never server data), and it self-registers
// its reset with the switchBand/logout scorch (step 4), like every Zustand store (stores.ts).
import { create } from "zustand";
import { registerStoreReset } from "./stores.js";

export type ToastTone = "error" | "success" | "info" | "warning";

export interface Toast {
  id: string;
  message: string;
  tone: ToastTone;
}

interface ToastState {
  toasts: Toast[];
  push: (message: string, tone?: ToastTone) => void;
  dismiss: (id: string) => void;
  reset: () => void;
}

export const useToasts = create<ToastState>((set) => ({
  toasts: [],
  push: (message, tone = "error") =>
    set((s) => ({ toasts: [...s.toasts, { id: crypto.randomUUID(), message, tone }] })),
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
  reset: () => set({ toasts: [] }),
}));

// Scorch on band switch / logout — a rollback toast from the old tenant must not linger over the new one.
registerStoreReset(() => useToasts.getState().reset());

/** Imperative push for non-component call sites (mutation onError handlers in the hooks layer). */
export function pushToast(message: string, tone: ToastTone = "error"): void {
  useToasts.getState().push(message, tone);
}
